import React, { useEffect, useState } from "react";
import { RefreshCw, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { onQueueChanged, queueSize, drainQueue } from "@/lib/poiQueue";
import FloatingSlot from "@/components/FloatingSlot";

/**
 * Persistent "Pending sync (n)" badge — Phase 3 · offline-queue drain UI.
 *
 * Rendered globally by the app shell. Hidden entirely when the queue is
 * empty so it never intrudes on normal usage. On reconnect we auto-drain
 * with a silent success toast; a manual "Sync now" button lets the user
 * force a retry.
 */
export default function PendingSyncBadge() {
  const [count, setCount] = useState(0);
  const [busy, setBusy]   = useState(false);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  // Refresh the count whenever the queue changes (add / remove / drain).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const n = await queueSize();
      if (!cancelled) setCount(n);
    };
    refresh();
    const unsub = onQueueChanged(refresh);
    const onOnline  = () => { setOnline(true); autoDrain(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      cancelled = true;
      unsub();
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runDrain = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await drainQueue((path, body) => api.post(path, body));
      if (r.ok > 0)     toast.success(`${r.ok} offline ${r.ok === 1 ? "tag" : "tags"} synced.`);
      if (r.failed > 0) toast.warning(`${r.failed} tag${r.failed === 1 ? "" : "s"} still pending — will retry.`);
      const n = await queueSize();
      setCount(n);
    } finally { setBusy(false); }
  };

  // Silent auto-drain — only surfaces a toast when at least one row
  // actually landed. Called on reconnect + when the badge mounts.
  const autoDrain = async () => {
    if (busy || (typeof navigator !== "undefined" && !navigator.onLine)) return;
    const before = await queueSize();
    if (before === 0) return;
    await runDrain();
  };

  useEffect(() => { autoDrain(); /* on mount */ }, []); // eslint-disable-line

  if (count === 0) return null;

  return (
    <FloatingSlot zone="bottom-left" order={1} size="badge" zIndex="badge" hideOnKeyboard={false}>
      <div
        className="rounded-full bg-white/95 backdrop-blur border border-stone-200 shadow-lg text-[11px] font-black text-stone-800 pl-3 pr-2 py-1.5 flex items-center gap-2"
        data-testid="pending-sync-badge"
      >
        {online ? (
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
        ) : (
          <WifiOff size={12} className="text-amber-600" />
        )}
        <span>
          Pending sync <span className="text-emerald-700">({count})</span>
        </span>
        <button
          onClick={runDrain}
          disabled={busy || !online}
          className="ml-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 disabled:opacity-40"
          data-testid="pending-sync-button"
        >
          {busy ? "Syncing…" : "Sync now"}
        </button>
      </div>
    </FloatingSlot>
  );
}
